import { DatePipe, NgClass, NgIf } from '@angular/common';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorSelectConfig } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {
  MatCell, MatCellDef, MatColumnDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef,
  MatRow, MatRowDef,
  MatTable, MatTableDataSource
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { RuleData } from '../../data-rules/data-rules-model';
import { DataRulesService } from '../../data-rules/data-rules.service';
import { SkeletonComponent } from '../../shared/skeleton/skeleton.component';
import { EnrichmentCreditService, IEnrichmentTransaction } from '../enrichment-credit.service';

export interface IEnrichmentTransactionRow {
  key: string;
  date: number;
  typeLabel: string;
  entity: string;
  recordCount: number | null;
  details: string;
  credits: number;
  balance: number;
  ruleName: string | null;
  ruleId: string | null;
  userId: string | null;
  userName: string | null;
  isTopup: boolean;
}

@Component({
  selector: 'app-enrichment-credit-history',
  templateUrl: './enrichment-credit-history.component.html',
  styleUrls: ['./enrichment-credit-history.component.scss'],
  imports: [
    SkeletonComponent,
    MatPaginator,
    MatSortModule,
    MatSort,
    MatTable,
    MatHeaderRow,
    MatRow,
    MatHeaderRowDef,
    MatRowDef,
    MatHeaderCell,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCellDef,
    NgClass,
    NgIf,
    DatePipe,
    MatTooltip
  ]
})
export class EnrichmentCreditHistoryComponent implements AfterViewInit {
  displayedColumns: string[] = ['date', 'type', 'ruleName', 'entity', 'userId', 'userName', 'recordCount', 'credits', 'balance'];
  dataSource: MatTableDataSource<IEnrichmentTransactionRow> = new MatTableDataSource<IEnrichmentTransactionRow>([]);
  loading = true;
  rules: RuleData[] = [];
  readonly pageSizeOptions: number[] = [5, 10, 25, 100];
  readonly pageSizeSelectConfig: MatPaginatorSelectConfig = { panelClass: 'kyloe-paginator-select-panel' };

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(private enrichmentCreditService: EnrichmentCreditService,
              private router: Router,
              private dataRulesService: DataRulesService) {
    window.scrollTo(0, 0);
    this.dataRulesService.getAllRules().subscribe(rules => {
      this.rules = rules;
      this.enrichmentCreditService.getTransactions().subscribe(transactions => {
        const rows = transactions.map((transaction, index) => this.toRow(transaction, index)).sort((a, b) => b.date - a.date);
        this.dataSource = new MatTableDataSource<IEnrichmentTransactionRow>(rows);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.loading = false;
      });
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  goToRule(entity: string, ruleId: string) {
    this.router.navigate(['/datarules'], { queryParams: { entity: entity, rule: ruleId } });
  }

  private toRow(transaction: IEnrichmentTransaction, index: number): IEnrichmentTransactionRow {
    const isTopup = !transaction.ruleId;
    const rule = this.getRule(transaction);
    return {
      key: `row-${index}-${transaction.date}`,
      date: transaction.date,
      typeLabel: isTopup ? 'Top-up' : 'Rule run',
      isTopup: isTopup,
      ruleId: transaction.ruleId,
      ruleName: isTopup ? null : (rule?.name || "Deleted rule"),
      entity: isTopup ? '-' : (rule?.source?.config?.entity || "Deleted rule"), // TODO: worth saving the entity in the transaction?
      recordCount: Object.values(transaction.creditCombinations || {}).reduce((sum, count) => sum + count, 0) || null,
      userId: transaction.userId,
      userName: transaction.userName,
      details: transaction.details || '',
      credits: transaction.totalAmount || 0,
      balance: transaction.balance || 0
    };
  }

  // the transactions already have ruleName as a field, but it could be out of date.
  private getRule(transaction: IEnrichmentTransaction): RuleData | null {
    if (transaction.ruleId) {
      return this.rules.find(rule => rule.id === transaction.ruleId) || null;
    } else {
      return null;
    }
  }
}
